import TextField from '@components/v2/form/TextField/TextField';
import Icon from '@components/v2/ui/Icon/Icon';
import ListItemButton from '@components/v2/ui/List/ListItemButton';
import ListItemIcon from '@components/v2/ui/List/ListItemIcon';
import ListItemText from '@components/v2/ui/List/ListItemText';
import Modal from '@components/v2/ui/Modal/Modal';
import { MODAL_BODY_PADDING } from '@components/v2/ui/Modal/modal.constants';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, CircularProgress, InputAdornment } from '@mui/material';
import { MessageSquarePlusIcon, Search } from 'lucide-react';
import { GroupedVirtuoso } from 'react-virtuoso';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import { TranslateType } from '../../../translation/translationContext.types';
import RachelChatHistoryItem from './components/RachelChatHistoryItem/RachelChatHistoryItem';
import { RachelChatGroup } from './rachelChatHistoryModal.utils';
import { useRachelChatHistoryModal } from './useRachelChatHistoryModal';

const CHAT_LIST_HEIGHT = 420;
const GROUP_HEADER_SPACING = 0.5;
const GROUP_HEADER_LEADING_SPACING = 2.5;
const MODAL_CHROME_HEIGHT = 272;
const CHAT_LIST_RESPONSIVE_HEIGHT = `min(${CHAT_LIST_HEIGHT}px, calc(100dvh - ${MODAL_CHROME_HEIGHT}px))`;
const CHAT_LIST_EDGE_GAP = 0.5;
const CHAT_LIST_BLEED = MODAL_BODY_PADDING - CHAT_LIST_EDGE_GAP;

interface RachelChatHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GROUP_LABEL_LANG: Record<'today' | 'yesterday', LangEnum> = {
  today: LangEnum.GLOBAL_DATE_TODAY,
  yesterday: LangEnum.RACHEL_CONVERSATIONS_YESTERDAY,
};

const getGroupLabel = (group: RachelChatGroup, translate: TranslateType) =>
  group.labelKey === 'older' ? group.dateLabel : translate(GROUP_LABEL_LANG[group.labelKey]);

const getGroupHeaderStyles = (groupIndex: number) => ({
  bgcolor: 'background.paper',
  pt: groupIndex === 0 ? GROUP_HEADER_SPACING : GROUP_HEADER_LEADING_SPACING,
  pb: GROUP_HEADER_SPACING,
  px: CHAT_LIST_BLEED,
});

const CHAT_LIST_STYLES = {
  height: CHAT_LIST_RESPONSIVE_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  mx: -CHAT_LIST_BLEED,
};

const CHAT_ROW_STYLES = { px: CHAT_LIST_BLEED };

const RachelChatHistoryModal = ({ isOpen, onClose }: RachelChatHistoryModalProps) => {
  const { translate } = useTranslation();
  const {
    groups,
    groupCounts,
    flatChats,
    isLoading,
    isSearching,
    isFetchingNextPage,
    fetchNextPage,
    search,
    setSearch,
    hasSearch,
    activeChatId,
    handleClose,
    handleSelect,
    handleNewConversation,
  } = useRachelChatHistoryModal(onClose);

  const hasChats = groupCounts.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      size="standard"
      header={{ title: translate(LangEnum.RACHEL_CONVERSATIONS_TITLE) }}
      onClose={handleClose}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={search}
          placeholder={translate(LangEnum.RACHEL_SEARCH_PLACEHOLDER)}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                  <Icon icon={Search} size="md" />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            },
            htmlInput: { 'aria-label': translate(LangEnum.RACHEL_SEARCH_PLACEHOLDER) },
          }}
        />
        {!hasSearch && (
          <ListItemButton testId="rachel-chat-history-new" onClick={handleNewConversation}>
            <ListItemIcon>
              <Icon icon={MessageSquarePlusIcon} size="md" />
            </ListItemIcon>
            <ListItemText primary={translate(LangEnum.RACHEL_START_NEW_CONVERSATION)} />
          </ListItemButton>
        )}
        <Box sx={CHAT_LIST_STYLES}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', px: CHAT_LIST_BLEED }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!isLoading && !hasChats && (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', px: CHAT_LIST_BLEED + 1 }}
              align="center"
            >
              {translate(LangEnum.RACHEL_CONVERSATIONS_EMPTY)}
            </Typography>
          )}
          {!isLoading && hasChats && (
            <GroupedVirtuoso
              style={{ height: '100%' }}
              groupCounts={groupCounts}
              computeItemKey={(index) => flatChats[index]?.id ?? index}
              endReached={() => void fetchNextPage()}
              components={{
                Footer: () =>
                  isFetchingNextPage ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 1.5,
                        px: CHAT_LIST_BLEED,
                      }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  ) : null,
              }}
              groupContent={(groupIndex) => (
                <Box sx={getGroupHeaderStyles(groupIndex)}>
                  <Typography variant="micro1" sx={{ color: 'text.secondary' }}>
                    {getGroupLabel(groups[groupIndex], translate)}
                  </Typography>
                </Box>
              )}
              itemContent={(index) => {
                const chat = flatChats[index];

                return (
                  <Box sx={CHAT_ROW_STYLES}>
                    <RachelChatHistoryItem
                      chat={chat}
                      isActive={chat.id === activeChatId}
                      onSelect={() => void handleSelect(chat.id)}
                    />
                  </Box>
                );
              }}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default RachelChatHistoryModal;
